<script setup lang="ts">
// App imports
import { useDebounceFn } from "#imports";

// Types
import type { FormTextInputType } from "../types/formtext";

const { debounce, type = "text" } = defineProps<{
  /**
   * Debounce time in milliseconds. Set to false to disable. Defaults to "undefined"
   */
  debounce?: string | number | false;
  /**
   * Type of input to render. Defaults to "text"
   */
  type?: FormTextInputType;
}>();

const field = useFormControl();

if (!field)
  throw new Error(
    "[FormText]: field instance not available. Did you wrap this component inside a FormControl?",
  );

const _name = unref(field.name);

const _handleModelChange = useDebounceFn(
  (value?: string) => {
    field.setValue(value || undefined, field.validateOnValueUpdate);
  },
  typeof debounce === "string" ? parseInt(`${debounce || 0}`) : debounce || 0,
);

const _inputModel = defineModel<string | null | undefined>({
  get: () => {
    if (field.value.value) {
      return typeof field.value.value === "string"
        ? field.value.value
        : String(field.value.value || "");
    }

    return undefined;
  },
  /**
   * Trigger possibly debounced update of vee-validate form value when v-model updates
   */
  set(value) {
    _handleModelChange(value ?? undefined);

    return value;
  },
});

defineExpose({
  field,
});
</script>

<template>
  <input
    v-model="_inputModel"
    :id="field.inputId || _name"
    class="form-text"
    :name="_name"
    :type="type"
  />
</template>

<style scoped>
.form-text {
  box-sizing: border-box;
  width: 100%;
  height: var(--form-input-height, 60px);
  font-size: var(--form-input-font-size, 1rem);
  line-height: var(--input-line-height, 1.5);
  border: var(--form-input-border-width, 1px) solid
    var(--form-input-border-color, #ddd);
  border-radius: var(--form-input-border-radius, 4px);
  padding-inline: var(--form-input-padding-inline, 16px);
  padding-block: var(--form-input-padding-block-start, 22px)
    var(--form-input-padding-block-end, 8px);
}
</style>
